/**
 * Rozstrzyganie skrótów klawiszowych odtwarzacza.
 *
 * Wydzielone z hooka jako **czysta funkcja**, i to nie z upodobania do
 * abstrakcji: całe ryzyko tego kodu siedzi w rozgałęzieniach — który klawisz
 * jest lokalny, który zmienia coś wspólnego, co wolno followerowi, kiedy skrót
 * ma nie zadziałać. Testowanie tego przez zdarzenia w DOM wymagałoby jsdom
 * i biblioteki do renderowania, a sprawdzałoby dokładnie to samo, tylko okrężnie.
 */

export type ShortcutAction =
  | { kind: 'none' }
  | { kind: 'fullscreen' }
  | { kind: 'subtitles' }
  | { kind: 'mute' }
  | { kind: 'volume'; delta: number }
  | { kind: 'playPause' }
  | { kind: 'seek'; deltaSec: number }
  | { kind: 'skip' }
  | { kind: 'nextEpisode' }
  | { kind: 'subtitleShift'; deltaSec: number };

/** Skok strzałką. Dziesięć sekund to długość typowego przejścia w dialogu. */
export const STEP_SEC = 10;

/** Skok z klawiszem Shift — mniej więcej scena. */
export const BIG_STEP_SEC = 60;

/** Krok głośności. Dziesięć procent daje dziesięć poziomów, co wystarcza. */
export const VOLUME_STEP = 0.1;

/** Krok przesunięcia napisów, zgodnie z opisem interfejsu. */
export const SUBTITLE_SHIFT_SEC = 0.25;

export interface ShortcutContext {
  shiftKey: boolean;
  /**
   * Czy wolno zmieniać pozycję i pauzę.
   *
   * W pokoju należą do prowadzącego: skrót followera rozjechałby wszystkich,
   * a jego własny takt korekty dryfu natychmiast cofnąłby zmianę — wyglądałoby
   * to na zepsuty odtwarzacz, a nie na brak uprawnienia.
   */
  canControlPlayback: boolean;
  /** Czy w tej chwili jest co pominąć. */
  hasSkip: boolean;
  hasNextEpisode: boolean;
  /** Czy jakakolwiek ścieżka napisów jest włączona. */
  hasActiveSubtitles: boolean;
}

/**
 * Klawisz plus kontekst → działanie.
 *
 * Klawisze **lokalne** (pełny ekran, napisy, głośność, wyciszenie) rozstrzygamy
 * PRZED sprawdzeniem uprawnień, bo nie dotyczą nikogo poza tym, kto je nacisnął.
 * Odwrotna kolejność odbierałaby followerowi w pokoju możliwość ściszenia.
 */
export function resolveShortcut(key: string, ctx: ShortcutContext): ShortcutAction {
  // Nawiasy zostawiamy bez zmiany wielkości: `[` i `]` nie mają wariantów.
  if (key === '[' || key === ']') {
    if (!ctx.hasActiveSubtitles) return { kind: 'none' };
    return {
      kind: 'subtitleShift',
      deltaSec: key === '[' ? -SUBTITLE_SHIFT_SEC : SUBTITLE_SHIFT_SEC,
    };
  }

  const k = key.toLowerCase();

  switch (k) {
    case 'f':
      return { kind: 'fullscreen' };
    case 'c':
      return { kind: 'subtitles' };
    case 'm':
      return { kind: 'mute' };
    case 'arrowup':
      return { kind: 'volume', delta: VOLUME_STEP };
    case 'arrowdown':
      return { kind: 'volume', delta: -VOLUME_STEP };
    default:
      break;
  }

  if (!ctx.canControlPlayback) return { kind: 'none' };

  const step = ctx.shiftKey ? BIG_STEP_SEC : STEP_SEC;

  switch (k) {
    case ' ':
    case 'k':
      return { kind: 'playPause' };
    case 'arrowleft':
    case 'j':
      return { kind: 'seek', deltaSec: -step };
    case 'arrowright':
    case 'l':
      return { kind: 'seek', deltaSec: step };
    case 's':
      // Bez zakresu do pominięcia klawisz musi nie robić nic, a nie przewijać
      // na oślep — inaczej `s` w losowym momencie skakałoby po odcinku.
      return ctx.hasSkip ? { kind: 'skip' } : { kind: 'none' };
    case 'n':
      return ctx.hasNextEpisode ? { kind: 'nextEpisode' } : { kind: 'none' };
    default:
      return { kind: 'none' };
  }
}

/**
 * Skróty do pokazania w pomocy.
 *
 * Lista jest tu, a nie w komponencie pomocy, z jednego powodu: **ma stać obok
 * kodu, który te klawisze obsługuje**. Trzymana w widoku rozjechałaby się przy
 * pierwszej zmianie mapowania, a pomoc, która kłamie o skrótach, jest gorsza od
 * braku pomocy — uczy nieufności do całego interfejsu.
 *
 * Podział na grupy odwzorowuje realne uprawnienia: część działa u każdego, część
 * wyłącznie u prowadzącego, bo zmienia to, co widzą wszyscy w pokoju.
 */
export const SHORTCUT_HELP: ReadonlyArray<{
  group: 'lokalne' | 'odtwarzanie' | 'napisy';
  keys: string;
  what: string;
}> = [
  { group: 'odtwarzanie', keys: 'Space / K', what: 'pauza i wznowienie' },
  { group: 'odtwarzanie', keys: '← / →', what: `przewijanie o ${STEP_SEC} s` },
  { group: 'odtwarzanie', keys: 'Shift + ← / →', what: `przewijanie o ${BIG_STEP_SEC} s` },
  { group: 'odtwarzanie', keys: 'J / L', what: `przewijanie o ${STEP_SEC} s bez strzałek` },
  { group: 'odtwarzanie', keys: 'S', what: 'pomiń opening albo ending' },
  { group: 'odtwarzanie', keys: 'N', what: 'następna pozycja kolejki' },
  { group: 'lokalne', keys: 'F', what: 'pełny ekran' },
  { group: 'lokalne', keys: 'M', what: 'wyciszenie' },
  { group: 'lokalne', keys: '↑ / ↓', what: 'głośność' },
  { group: 'lokalne', keys: 'C', what: 'napisy włącz i wyłącz' },
  { group: 'napisy', keys: '[ / ]', what: `przesunięcie napisów o ${SUBTITLE_SHIFT_SEC} s` },
];
